// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import { playMessageNotification, isDocumentVisible } from '../lib/chatNotifications';
import { logger } from '../lib/logger';
import { DeliveryStatus } from '../types/chat';
import type { ChatMessage, ChatReactionUpdate } from '../types/chat';

interface ChatStore {
  // Chat state
  messages: ChatMessage[];
  messageIdSet: Set<string>; // O(1) dedup lookup by message ID
  messageContentMap: Map<string, string>; // O(1) dedup lookup by hash(player+content+time) -> messageId
  typingPlayers: string[];
  isOpen: boolean;
  unreadCount: number;
  soundEnabled: boolean;
  pendingMessageIds: Set<string>;

  // Actions
  addMessage: (message: ChatMessage) => void;
  addPendingMessage: (message: ChatMessage) => void;
  confirmDelivery: (messageId: string) => void;
  markDeliveryFailed: (messageId: string) => void;
  setMessages: (messages: ChatMessage[]) => void;
  updateTypingPlayers: (players: string[]) => void;
  updateReactions: (update: ChatReactionUpdate) => void;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  clearUnread: () => void;
  incrementUnread: () => void;
  clearChat: () => void;
  toggleSound: () => void;
}

export const useChatStore = create<ChatStore>()((set, get) => ({
  // Initial state
  messages: [],
  messageIdSet: new Set<string>(),
  messageContentMap: new Map<string, string>(),
  typingPlayers: [],
  isOpen: false,
  unreadCount: 0,
  soundEnabled: true,
  pendingMessageIds: new Set<string>(),

  // Actions
  addMessage: message => {
    const state = get();
    const pendingIds = state.pendingMessageIds;

    // If this message matches a pending message, confirm delivery
    if (pendingIds.has(message.message_id)) {
      logger.debug('chat_message_delivered', { messageId: message.message_id });
      get().confirmDelivery(message.message_id);
      return; // Don't add duplicate
    }

    // Check if message already exists by ID (O(1) Set lookup)
    if (state.messageIdSet.has(message.message_id)) {
      logger.debug('chat_message_duplicate_id_skipped', {
        messageId: message.message_id,
      });
      return; // Don't add duplicate
    }

    // Check for duplicate by content + timestamp + player (catches backend duplicates) using Map
    // We bucket timestamps by second to catch near-simultaneous duplicates
    const timestampSec = Math.floor(new Date(message.timestamp).getTime() / 1000);
    const contentHash = `${message.player_id}:${message.content}:${timestampSec}`;

    // Also check the previous and next second in case of boundary conditions
    const contentHashPrev = `${message.player_id}:${message.content}:${timestampSec - 1}`;
    const contentHashNext = `${message.player_id}:${message.content}:${timestampSec + 1}`;

    const existingId =
      state.messageContentMap.get(contentHash) ||
      state.messageContentMap.get(contentHashPrev) ||
      state.messageContentMap.get(contentHashNext);

    if (existingId) {
      logger.debug('chat_message_duplicate_content_skipped', {
        existingId,
        newId: message.message_id,
        content: message.content?.substring(0, 50),
      });
      return; // Don't add duplicate
    }

    logger.debug('chat_message_added', { messageId: message.message_id });
    set(state => {
      const newMap = new Map(state.messageContentMap);
      newMap.set(contentHash, message.message_id);
      return {
        messages: [...state.messages, message],
        messageIdSet: new Set([...state.messageIdSet, message.message_id]),
        messageContentMap: newMap,
      };
    });
    // Increment unread if chat is closed
    if (!get().isOpen) {
      get().incrementUnread();

      // Play notification sound if enabled and page is not focused
      if (get().soundEnabled && !isDocumentVisible()) {
        playMessageNotification();
      }
    }
  },

  addPendingMessage: message => {
    logger.debug('chat_pending_message_added', {
      messageId: message.message_id,
    });
    const pendingMessage = {
      ...message,
      delivery_status: DeliveryStatus.SENDING,
    };
    const timestampSec = Math.floor(new Date(message.timestamp).getTime() / 1000);
    const contentHash = `${message.player_id}:${message.content}:${timestampSec}`;

    set(state => {
      const newMap = new Map(state.messageContentMap);
      newMap.set(contentHash, message.message_id);
      return {
        messages: [...state.messages, pendingMessage],
        messageIdSet: new Set([...state.messageIdSet, message.message_id]),
        messageContentMap: newMap,
        pendingMessageIds: new Set([...state.pendingMessageIds, message.message_id]),
      };
    });
  },

  confirmDelivery: messageId => {
    logger.debug('chat_delivery_confirmed', { messageId });
    set(state => {
      const newPendingIds = new Set(state.pendingMessageIds);
      newPendingIds.delete(messageId);
      return {
        messages: state.messages.map(msg =>
          msg.message_id === messageId ? { ...msg, delivery_status: DeliveryStatus.DELIVERED } : msg
        ),
        pendingMessageIds: newPendingIds,
      };
    });
  },

  markDeliveryFailed: messageId => {
    logger.debug('chat_delivery_failed', { messageId });
    set(state => {
      const newPendingIds = new Set(state.pendingMessageIds);
      newPendingIds.delete(messageId);
      return {
        messages: state.messages.map(msg =>
          msg.message_id === messageId ? { ...msg, delivery_status: DeliveryStatus.FAILED } : msg
        ),
        pendingMessageIds: newPendingIds,
      };
    });
  },

  setMessages: messages => {
    logger.debug('chat_history_loaded', { count: messages.length });
    // Empty array = clear operation (reset)
    if (messages.length === 0) {
      set({
        messages: [],
        messageIdSet: new Set<string>(),
        messageContentMap: new Map<string, string>(),
      });
      return;
    }
    // Merge with existing messages to preserve any that arrived via live broadcast
    // but might not be in history yet (race condition)
    set(state => {
      const historyIds = new Set(messages.map(m => m.message_id));
      // Keep any existing messages that aren't in history (recently arrived)
      const recentMessages = state.messages.filter(m => !historyIds.has(m.message_id));
      // Combine: history first, then any recent messages not in history
      const merged = [...messages, ...recentMessages];
      // Sort by timestamp to maintain order
      merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      logger.debug('chat_messages_merged', {
        historyCount: messages.length,
        recentCount: recentMessages.length,
        totalCount: merged.length,
      });

      const newMap = new Map<string, string>();
      merged.forEach(m => {
        const timestampSec = Math.floor(new Date(m.timestamp).getTime() / 1000);
        newMap.set(`${m.player_id}:${m.content}:${timestampSec}`, m.message_id);
      });

      return {
        messages: merged,
        messageIdSet: new Set(merged.map(m => m.message_id)),
        messageContentMap: newMap,
      };
    });
  },

  updateTypingPlayers: players => {
    logger.debug('typing_players_updated', { players });
    set({ typingPlayers: players });
  },

  updateReactions: update => {
    logger.debug('reactions_updated', { update });
    set(state => ({
      messages: state.messages.map(msg =>
        msg.message_id === update.message_id ? { ...msg, reactions: update.reactions } : msg
      ),
    }));
  },

  toggleChat: () => {
    set(state => {
      const newIsOpen = !state.isOpen;
      logger.debug('chat_toggled', { isOpen: newIsOpen });
      return {
        isOpen: newIsOpen,
        unreadCount: newIsOpen ? 0 : state.unreadCount,
      };
    });
  },

  openChat: () => {
    logger.debug('chat_opened');
    set({ isOpen: true, unreadCount: 0 });
  },

  closeChat: () => {
    logger.debug('chat_closed');
    set({ isOpen: false });
  },

  clearUnread: () => {
    set({ unreadCount: 0 });
  },

  incrementUnread: () => {
    set(state => ({ unreadCount: state.unreadCount + 1 }));
  },

  clearChat: () => {
    logger.debug('chat_cleared');
    set({
      messages: [],
      messageIdSet: new Set<string>(),
      messageContentMap: new Map<string, string>(),
      typingPlayers: [],
      unreadCount: 0,
      pendingMessageIds: new Set<string>(),
    });
  },

  toggleSound: () => {
    set(state => {
      const newSoundEnabled = !state.soundEnabled;
      logger.debug('chat_sound_toggled', { soundEnabled: newSoundEnabled });
      return { soundEnabled: newSoundEnabled };
    });
  },
}));

// Expose store on window for E2E testing (development only)
if (typeof window !== 'undefined') {
  (window as unknown as { useChatStore: typeof useChatStore }).useChatStore = useChatStore;
}
